import React, { useCallback, useEffect, useState } from "react";
import Layout from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Plus, Power, Trash2, Lock, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const empty = {
  name: "", peer_address: "", local_subnet: "10.0.0.0/16", remote_subnet: "192.168.0.0/16",
  psk: "", ike_version: "ikev2", encryption: "aes-256-gcm", integrity: "sha256",
  dh_group: "19", pfs: true, rekey_minutes: 60,
  split_tunnel: false, split_tunnel_routes: [], qos_class: "default",
  enabled: true, description: "",
};

export default function Vpn() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/vpn/tunnels"); setItems(data); }
    catch (err) { console.error("vpn load failed", err); }
  }, []);
  useEffect(() => { load(); const i = setInterval(load, 6000); return () => clearInterval(i); }, [load]);

  const submit = async () => {
    try {
      const body = { ...form, split_tunnel_routes: typeof form.split_tunnel_routes === "string"
        ? form.split_tunnel_routes.split(",").map((s) => s.trim()).filter(Boolean) : (form.split_tunnel_routes || []) };
      if (editing) await api.put(`/vpn/tunnels/${editing}`, body);
      else await api.post("/vpn/tunnels", body);
      toast.success(editing ? "Tunnel updated" : "Tunnel created");
      setOpen(false); setEditing(null); setForm(empty); await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const connect = async (id) => { await api.post(`/vpn/tunnels/${id}/connect`); toast.success("Connecting..."); load(); };
  const disconnect = async (id) => { await api.post(`/vpn/tunnels/${id}/disconnect`); toast.success("Disconnected"); load(); };
  const del = async (id) => {
    if (!window.confirm("Delete tunnel?")) return;
    await api.delete(`/vpn/tunnels/${id}`); toast.success("Removed"); load();
  };
  const startEdit = (v) => {
    const { tunnel_id, status, last_handshake, rx_bytes, tx_bytes, created_at, updated_at, created_by, ...rest } = v;
    setForm({ ...rest, split_tunnel_routes: (rest.split_tunnel_routes || []).join(", "), psk: "" });
    setEditing(tunnel_id); setOpen(true);
  };

  return (
    <Layout title="VPN Tunnels" subtitle="IPSEC SITE-TO-SITE">
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs font-mono text-zinc-500 uppercase tracking-widest" data-testid="vpn-count">
          {items.length} TUNNELS · {items.filter((x) => x.status === "up").length} UP
        </div>
        {user?.role === "admin" && (
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(empty); } }}>
            <DialogTrigger asChild>
              <Button data-testid="add-vpn-btn" className="bg-cyan-500 hover:bg-cyan-400 text-zinc-950 rounded-sm gap-2">
                <Plus className="w-4 h-4" /> NEW TUNNEL
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-950 border-zinc-800 rounded-sm max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Lock className="w-4 h-4 text-cyan-400" /> {editing ? "Edit" : "Create"} Tunnel</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3 py-2">
                <div className="col-span-2"><L>Name</L><Input data-testid="vpn-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-zinc-900 border-zinc-800 rounded-sm" /></div>
                <div><L>Peer Address</L><Input data-testid="vpn-peer" value={form.peer_address} onChange={(e) => setForm({ ...form, peer_address: e.target.value })} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" placeholder="203.0.113.4" /></div>
                <div><L>Pre-Shared Key</L><Input data-testid="vpn-psk" type="password" value={form.psk} onChange={(e) => setForm({ ...form, psk: e.target.value })} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" placeholder={editing ? "(unchanged)" : ""} /></div>
                <div><L>Local Subnet</L><Input data-testid="vpn-local" value={form.local_subnet} onChange={(e) => setForm({ ...form, local_subnet: e.target.value })} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" /></div>
                <div><L>Remote Subnet</L><Input data-testid="vpn-remote" value={form.remote_subnet} onChange={(e) => setForm({ ...form, remote_subnet: e.target.value })} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" /></div>
                <div><L>IKE Version</L><Sel testid="vpn-ike" value={form.ike_version} onChange={(v) => setForm({ ...form, ike_version: v })} options={["ikev1", "ikev2"]} /></div>
                <div><L>Encryption</L><Sel testid="vpn-enc" value={form.encryption} onChange={(v) => setForm({ ...form, encryption: v })} options={["aes-256-gcm", "aes-128-gcm", "chacha20-poly1305"]} /></div>
                <div><L>Integrity</L><Sel testid="vpn-int" value={form.integrity} onChange={(v) => setForm({ ...form, integrity: v })} options={["sha256", "sha384", "sha512"]} /></div>
                <div><L>DH Group</L><Sel testid="vpn-dh" value={form.dh_group} onChange={(v) => setForm({ ...form, dh_group: v })} options={["14", "19", "20", "21"]} /></div>
                <div><L>QoS Class</L><Sel testid="vpn-qos" value={form.qos_class} onChange={(v) => setForm({ ...form, qos_class: v })} options={["voice", "video", "interactive", "bulk", "default"]} /></div>
                <div><L>Rekey (min)</L><Input data-testid="vpn-rekey" type="number" value={form.rekey_minutes} onChange={(e) => setForm({ ...form, rekey_minutes: parseInt(e.target.value || "0", 10) })} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" /></div>
                <div className="col-span-2 grid grid-cols-3 gap-3">
                  <ToggleBox label="Enabled" testid="vpn-enabled" value={form.enabled} onChange={(v) => setForm({ ...form, enabled: v })} />
                  <ToggleBox label="PFS" testid="vpn-pfs" value={form.pfs} onChange={(v) => setForm({ ...form, pfs: v })} />
                  <ToggleBox label="Split Tunnel" testid="vpn-split" value={form.split_tunnel} onChange={(v) => setForm({ ...form, split_tunnel: v })} />
                </div>
                {form.split_tunnel && (
                  <div className="col-span-2"><L>Split Tunnel Routes (comma-separated CIDRs)</L>
                    <Input data-testid="vpn-routes" value={form.split_tunnel_routes} onChange={(e) => setForm({ ...form, split_tunnel_routes: e.target.value })}
                      className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" placeholder="192.168.10.0/24, 10.50.0.0/16" />
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                <Button data-testid="vpn-submit" onClick={submit} className="bg-cyan-500 hover:bg-cyan-400 text-zinc-950 rounded-sm">{editing ? "SAVE" : "CREATE"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="vpn-list">
        {items.map((v) => (
          <div key={v.tunnel_id} data-testid={`vpn-card-${v.tunnel_id}`} className="border border-zinc-800 rounded-sm bg-zinc-900/60 p-4 fade-up">
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="font-display text-lg font-bold tracking-tight">{v.name}</div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-0.5">{v.tunnel_id}</div>
              </div>
              <span className={"text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border rounded-sm flex items-center gap-1.5 " +
                (v.status === "up" ? "text-emerald-400 border-emerald-500/40 bg-emerald-500/10"
                  : "text-zinc-500 border-zinc-700")}>
                <span className={v.status === "up" ? "live-dot" : "live-dot amber"} /> {v.status}
              </span>
            </div>
            <div className="font-mono text-[11px] grid grid-cols-2 gap-y-1.5 gap-x-4">
              <Field k="Peer" v={v.peer_address} />
              <Field k="Local" v={v.local_subnet} />
              <Field k="Remote" v={v.remote_subnet} />
              <Field k="Cipher" v={v.encryption} hl />
              <Field k="IKE" v={v.ike_version} />
              <Field k="DH" v={v.dh_group} />
              <Field k="QoS" v={v.qos_class} />
              <Field k="Split-Tunnel" v={v.split_tunnel ? "ON" : "OFF"} hl={v.split_tunnel} />
            </div>
            {v.split_tunnel && (v.split_tunnel_routes || []).length > 0 && (
              <div className="mt-2 text-[10px] font-mono text-zinc-400">
                <span className="text-zinc-500 uppercase tracking-widest">routes:</span> {(v.split_tunnel_routes || []).join(", ")}
              </div>
            )}
            {user?.role === "admin" && (
              <div className="flex items-center gap-2 mt-4 pt-3 border-t border-zinc-800">
                {v.status === "up" ? (
                  <Button size="sm" variant="ghost" data-testid={`vpn-disconnect-${v.tunnel_id}`} onClick={() => disconnect(v.tunnel_id)} className="gap-1.5 text-amber-400"><Power className="w-3.5 h-3.5" />Disconnect</Button>
                ) : (
                  <Button size="sm" variant="ghost" data-testid={`vpn-connect-${v.tunnel_id}`} onClick={() => connect(v.tunnel_id)} className="gap-1.5 text-emerald-400"><Power className="w-3.5 h-3.5" />Connect</Button>
                )}
                <Button size="sm" variant="ghost" data-testid={`vpn-edit-${v.tunnel_id}`} onClick={() => startEdit(v)} className="gap-1.5"><Pencil className="w-3.5 h-3.5" />Edit</Button>
                <Button size="sm" variant="ghost" data-testid={`vpn-delete-${v.tunnel_id}`} onClick={() => del(v.tunnel_id)} className="gap-1.5 text-red-400"><Trash2 className="w-3.5 h-3.5" />Delete</Button>
              </div>
            )}
          </div>
        ))}
        {items.length === 0 && <div className="text-zinc-500 font-mono text-sm">no tunnels yet.</div>}
      </div>
    </Layout>
  );
}

function L({ children }) { return <Label className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">{children}</Label>; }
function Sel({ value, onChange, options, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono uppercase text-xs"><SelectValue /></SelectTrigger>
      <SelectContent className="bg-zinc-950 border-zinc-800">{options.map((o) => <SelectItem key={o} value={o} className="font-mono uppercase text-xs">{o}</SelectItem>)}</SelectContent>
    </Select>
  );
}
function ToggleBox({ label, value, onChange, testid }) {
  return (
    <div className="flex items-center justify-between border border-zinc-800 rounded-sm px-3 py-2 bg-zinc-900">
      <span className="text-sm">{label}</span>
      <Switch data-testid={testid} checked={value} onCheckedChange={onChange} />
    </div>
  );
}
function Field({ k, v, hl }) {
  return (
    <div>
      <span className="text-zinc-500 uppercase tracking-widest text-[10px] mr-2">{k}</span>
      <span className={hl ? "text-cyan-400" : "text-zinc-300"}>{v}</span>
    </div>
  );
}
